import Axios from 'axios';

import {
    canCreateSeason,
    normalizeSeason,
    normalizeSeasonDetails,
    sortSeasonsDesc,
    toActiveShowSeason,
} from './showSeasonsHelpers';

const hasBookerId = (bookerId) => bookerId != null && bookerId !== '' && bookerId !== undefined;

const normalizeOptionalNotes = (notes) => {
    if (notes == null) {
        return null;
    }

    const trimmedNotes = String(notes).trim();
    return trimmedNotes || null;
};

const mapSeasonModelPayload = ({ modelId, bookerId = null, notes = '' }) => {
    const payload = {
        ModelID: Number(modelId),
    };

    payload.BookerID = hasBookerId(bookerId) ? Number(bookerId) : null;
    payload.Notes = normalizeOptionalNotes(notes);

    return payload;
};

const buildSeasonModelUpdatePayload = (seasonModel, values) => ({
    ID: seasonModel.ID,
    ModelID: seasonModel.ModelID,
    Status: values.Status,
    BookerID: hasBookerId(values.BookerID) ? Number(values.BookerID) : null,
    Notes: normalizeOptionalNotes(values.Notes),
});

const withSeasonId = (seasonModel, seasonId) => ({
    ...seasonModel,
    SeasonID: seasonModel.SeasonID ?? Number(seasonId),
});

export const loadSeasons = async () => {
    const seasons = await Axios.get('/seasons');
    return sortSeasonsDesc((seasons || []).map(normalizeSeason));
};

export const loadShowSeasonDetails = async (seasonId) => {
    if (!seasonId) {
        return null;
    }

    const details = await Axios.get(`/seasons/${seasonId}`);
    return normalizeSeasonDetails(details);
};

export const loadActiveShowSeasonsForContext = async () => {
    const seasons = await Axios.get('/seasons/active');

    return (seasons || []).map((season) => {
        const normalizedSeason = normalizeSeason(season);
        const modelIds = (season.Models || []).map((modelId) => Number(modelId));

        return toActiveShowSeason(normalizedSeason, modelIds);
    });
};

export const createSeason = async (values, currentUser) => {
    const seasons = await loadSeasons();

    if (!canCreateSeason(seasons, currentUser.ActiveContext)) {
        throw new Error('You must archive a season before creating a new one. Maximum 6 active seasons allowed.');
    }

    const season = await Axios.post('/seasons', {
        Name: values.Name,
        StartDate: values.StartDate || null,
        EndDate: values.EndDate || null,
        Description: values.Description || '',
    });

    return normalizeSeason(season);
};

export const updateSeason = async (seasonId, values) => {
    const season = await Axios.put('/seasons', {
        ID: Number(seasonId),
        Name: values.Name,
        StartDate: values.StartDate || null,
        EndDate: values.EndDate || null,
        Description: values.Description || '',
        DepartmentCreated: values.DepartmentCreated ?? null,
    });

    return normalizeSeason(season);
};

export const archiveSeason = async (season, archive = true) => {
    await Axios.put(`/seasons/${season.ID}/archived`, {
        Archived: archive,
    });

    return normalizeSeason({
        ...season,
        Archived: archive,
    });
};

export const deleteSeason = async (seasonId) => {
    await Axios.delete(`/seasons/${seasonId}`);
};

export const createSeasonModels = async (seasonId, items) => {
    if (!items?.length) {
        return [];
    }

    const seasonModels = await Axios.post(
        `/seasons/${seasonId}/models`,
        items.map(mapSeasonModelPayload)
    );

    return (seasonModels || []).map((seasonModel) => withSeasonId(seasonModel, seasonId));
};

export const createSeasonModel = async (seasonId, modelId, { bookerId = null, notes = '' } = {}) => {
    const seasonModels = await createSeasonModels(seasonId, [{ modelId, bookerId, notes }]);
    return seasonModels[0] || null;
};

export const createSeasonModelsBulk = async (seasonId, modelIds) => {
    if (!modelIds?.length) {
        return [];
    }

    return createSeasonModels(
        seasonId,
        modelIds.map((modelId) => ({ modelId, bookerId: null, notes: '' }))
    );
};

export const updateSeasonModel = async (seasonId, seasonModel, values) => {
    const updatedSeasonModel = await Axios.put(
        `/seasons/${seasonId}/models`,
        buildSeasonModelUpdatePayload(seasonModel, values)
    );

    return withSeasonId(updatedSeasonModel, seasonId);
};

export const updateSeasonModelStatus = async (seasonModel, status) => {
    await Axios.put(`/seasons/${seasonModel.SeasonID}/models/${seasonModel.ID}/status`, {
        Status: status,
    });

    return withSeasonId(
        {
            ...seasonModel,
            Status: status,
        },
        seasonModel.SeasonID
    );
};

export const deleteSeasonModel = async (seasonId, seasonModelId) => {
    await Axios.delete(`/seasons/${seasonId}/models/${seasonModelId}`);
};
